import { useState, useEffect } from 'react';
import { Layout } from '@/components/common/Layout/Layout';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { SearchInput } from '@/components/ui/search-input';
import { Plus, FileText, ExternalLink, Copy, RefreshCw, Trash2, CheckCircle, Clock, Files, Shield } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { showErrorToast } from '@/lib/api';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { AMLFormDialog } from './AMLFormDialog';
import type { AMLForm } from '@/types/aml-forms';
import {
  getAMLForms,
  deleteAMLForm,
  regenerateAMLFormToken,
  toggleAMLFormChecked,
} from '@/services/aml-forms';
import { useDateFormat } from '@/lib/dates';

export function AMLForms() {
  const { toast } = useToast();
  const { format: formatDisplayDate } = useDateFormat();
  const [isLoading, setIsLoading] = useState(false);
  const [forms, setForms] = useState<AMLForm[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isFormDialogOpen, setIsFormDialogOpen] = useState(false);
  const [selectedForm, setSelectedForm] = useState<AMLForm | null>(null);
  const [templateForm, setTemplateForm] = useState<AMLForm | null>(null);
  const [deleteFormId, setDeleteFormId] = useState<number | null>(null);

  const loadForms = async () => {
    setIsLoading(true);
    try {
      const response = await getAMLForms();
      setForms(response.results);
    } catch (error) {
      showErrorToast(error, 'Failed to load AML forms');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadForms();
  }, []);

  const handleCreateForm = () => {
    setSelectedForm(null);
    setTemplateForm(null);
    setIsFormDialogOpen(true);
  };

  const handleEditForm = (form: AMLForm) => {
    setSelectedForm(form);
    setTemplateForm(null);
    setIsFormDialogOpen(true);
  };

  const handleCopyFormStructure = (form: AMLForm) => {
    setSelectedForm(null);
    setTemplateForm(form);
    setIsFormDialogOpen(true);
    toast({
      title: 'Template Loaded',
      description: `Using "${form.name}" as a template. Select a client and customize as needed.`,
    });
  };

  const handleFormSaved = () => {
    setIsFormDialogOpen(false);
    setSelectedForm(null);
    setTemplateForm(null);
    loadForms();
  };

  const handleDeleteClick = (formId: number) => {
    setDeleteFormId(formId);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteFormId) return;

    try {
      await deleteAMLForm(deleteFormId);
      toast({
        title: 'Success',
        description: 'AML form deleted successfully',
      });
      loadForms();
    } catch (error) {
      showErrorToast(error, 'Failed to delete AML form');
    } finally {
      setDeleteFormId(null);
    }
  };

  const handleCopyLink = async (publicUrl: string) => {
    const fullUrl = `${window.location.origin}${publicUrl}`;
    try {
      await navigator.clipboard.writeText(fullUrl);
      toast({
        title: 'Success',
        description: 'Link copied to clipboard',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to copy link',
        variant: 'destructive',
      });
    }
  };

  const handleRegenerateToken = async (formId: number) => {
    try {
      await regenerateAMLFormToken(formId);
      toast({
        title: 'Success',
        description: 'Token regenerated successfully. Previous link is now invalid.',
      });
      loadForms();
    } catch (error) {
      showErrorToast(error, 'Failed to regenerate token');
    }
  };

  const handleToggleChecked = async (formId: number, currentChecked: boolean) => {
    try {
      await toggleAMLFormChecked(formId, !currentChecked);
      toast({
        title: 'Success',
        description: `Form marked as ${!currentChecked ? 'approved' : 'not approved'}`,
      });
      loadForms();
    } catch (error) {
      showErrorToast(error, 'Failed to update approval status');
    }
  };

  const filteredForms = forms.filter(
    (form) =>
      form.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      form.client_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      form.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getStatusBadge = (form: AMLForm) => {
    if (form.is_completed) {
      return (
        <Badge className="bg-green-500 hover:bg-green-600">
          <CheckCircle className="w-3 h-3 mr-1" />
          Completed
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="border-yellow-500 text-yellow-600">
        <Clock className="w-3 h-3 mr-1" />
        Pending
      </Badge>
    );
  };

  return (
    <Layout>
      <div className="container mx-auto py-6 space-y-6">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-2xl font-bold">AML Forms</CardTitle>
                <CardDescription>
                  Manage Anti-Money Laundering forms for your clients
                </CardDescription>
              </div>
              <Button onClick={handleCreateForm} size="sm">
                <Plus className="w-4 h-4 mr-2" />
                Create Form
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="mb-4">
              <SearchInput
                placeholder="Search by client name, form name, or description..."
                value={searchQuery}
                onChange={setSearchQuery}
              />
            </div>

            {isLoading ? (
              <div className="text-center py-8">Loading...</div>
            ) : filteredForms.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                {searchQuery ? 'No forms found matching your search' : 'No AML forms yet. Create one to get started.'}
              </div>
            ) : (
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Client</TableHead>
                      <TableHead>Form Name</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Approved</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead>Completed</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredForms.map((form) => (
                      <TableRow key={form.id}>
                        <TableCell className="font-medium">{form.client_name}</TableCell>
                        <TableCell>{form.name}</TableCell>
                        <TableCell className="max-w-xs truncate">{form.description}</TableCell>
                        <TableCell>{getStatusBadge(form)}</TableCell>
                        <TableCell>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant={form.is_checked ? "default" : "outline"}
                                  size="sm"
                                  onClick={() => handleToggleChecked(form.id, form.is_checked)}
                                  className={form.is_checked ? "bg-green-600 hover:bg-green-700" : ""}
                                >
                                  <Shield className={`w-4 h-4 ${form.is_checked ? '' : 'text-gray-400'}`} />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                {form.is_checked ? 'Mark as Not Approved' : 'Mark as Approved'}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </TableCell>
                        <TableCell>
                          {formatDisplayDate(form.created_at)}
                        </TableCell>
                        <TableCell>
                          {form.completed_at
                            ? formatDisplayDate(form.completed_at)
                            : '-'}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-end gap-2">
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleEditForm(form)}
                                  >
                                    <FileText className="w-4 h-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>View/Edit Form</TooltipContent>
                              </Tooltip>

                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleCopyFormStructure(form)}
                                  >
                                    <Files className="w-4 h-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Copy as Template</TooltipContent>
                              </Tooltip>

                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleCopyLink(form.public_url)}
                                  >
                                    <Copy className="w-4 h-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Copy Public Link</TooltipContent>
                              </Tooltip>

                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => window.open(form.public_url, '_blank')}
                                  >
                                    <ExternalLink className="w-4 h-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Open Public Link</TooltipContent>
                              </Tooltip>

                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleRegenerateToken(form.id)}
                                  >
                                    <RefreshCw className="w-4 h-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Regenerate Token</TooltipContent>
                              </Tooltip>

                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleDeleteClick(form.id)}
                                  >
                                    <Trash2 className="w-4 h-4 text-destructive" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Delete Form</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        <AMLFormDialog
          open={isFormDialogOpen}
          onOpenChange={setIsFormDialogOpen}
          form={selectedForm}
          templateForm={templateForm}
          onSaved={handleFormSaved}
        />

        <AlertDialog open={!!deleteFormId} onOpenChange={() => setDeleteFormId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete AML Form</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this AML form? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeleteConfirm}>Delete</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
